<template>
  <div class="w-full h-full">
    <!-- 顶部 Banner（和列表页一样） -->
    <div class="relative w-full h-[200px] sm:h-[300px] md:h-[400px]">
      <img
        src="../../assets/img/news_bg.webp"
        alt="service-banner"
        class="w-full object-cover h-full"
      />
      <div class="absolute bottom-0 left-0 right-0 text-white text-[32px] sm:text-[48px] md:text-[60px] lg:text-[72px] font-bold container mx-auto flex flex-col items-start justify-center px-4 sm:px-6">
        <div>资讯中心</div>
        <div class="text-[20px] sm:text-[28px] md:text-[36px] lg:text-[48px] -mt-[8px] sm:-mt-[12px] md:-mt-[16px] lg:-mt-[20px] mb-4 sm:mb-6 md:mb-8 lg:mb-10">Information Center</div>
      </div>
    </div>

    <!-- 正文内容区域 -->
    <div class="container mx-auto pb-8 pt-4 sm:pt-6 md:pt-8 lg:pt-[67px] max-w-[1200px] px-4 sm:px-6">
      <!-- 加载中状态 -->
      <div
        v-if="loading"
        class="text-center py-12"
      >
        <div class="text-[#333333] text-lg">加载中...</div>
      </div>

      <!-- 新闻详情内容 -->
      <template v-else-if="newsDetail">
        <!-- 标题 -->
        <h1 class="text-[#333333] text-[22px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-black mb-3 sm:mb-4 leading-tight scroll-fade-in
        
        text-center">
          {{ newsDetail.title }}
        </h1>

        <!-- 日期和来源 -->
        <div class="text-[#333333] mb-2 scroll-fade-in text-center">
          <div class="mb-2  text-[14px] sm:text-[16px] lg:text-[14px] font-bold">{{ newsDetail.dateTime }}</div>
          <div class="font-black  text-[14px] sm:text-[16px] lg:text-[18px]  mb-2 lg:mb-8">{{ newsDetail.newsType || '综合多方行业资讯及企业官方发布信息' }}</div>
        </div>

        <!-- 图片 -->
        <div
          v-if="newsDetail.image"
          class="mb-6 sm:mb-8 flex justify-center scroll-fade-in"
        >
          <img
            :src="newsDetail.image"
            :alt="newsDetail.title"
            class="w-full max-w-[800px] h-auto object-cover rounded-[8px]"
            @error="handleImageError"
          />
        </div>

        <!-- 正文内容 -->
        <div
          class="news-content scroll-fade-in"
          v-html="newsDetail.content"
        ></div>
        <!-- 声明 -->
        <div
          v-if="newsDetail.statement"
          class="news-statement scroll-fade-in"
        >
          {{ newsDetail.statement }}
        </div>
      </template>

      <!-- 空状态 -->
      <div
        v-else
        class="text-center py-12"
      >
        <div class="text-[#333333] text-lg">新闻不存在或已删除</div>
      </div>
      <!-- 很好看的分割线 -->
      <div class="w-full h-[1px] bg-[#808080] lg:mt-[113px] mt-4"></div>
      <!-- 底部导航按钮 -->
      <div
        v-if="newsDetail"
        class="flex justify-center gap-3 sm:gap-6 mt-4 sm:mt-10 md:mt-8 sm:mb-12 scroll-fade-in"
      >
        <button
          @click="goToPrevious"
          :disabled="!hasPrevious"
          class="nav-btn"
          :class="{ 'disabled': !hasPrevious }"
        >
          上一篇
        </button>
        <button
          @click="goToNext"
          :disabled="!hasNext"
          class="nav-btn"
          :class="{ 'disabled': !hasNext }"
        >
          下一篇
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { newsApi, type NewsDetail } from "@/api";
import { RequestError } from "@/utils/request";

const route = useRoute();
const router = useRouter();

// 设置 meta 标签的辅助函数
const setMeta = (name: "keywords" | "description", content?: string) => {
  const value = (content || "").trim();
  if (!value) return;
  let tag = document.querySelector(
    `meta[name="${name}"]`
  ) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
};

// 从路由参数获取新闻ID
const newsId = computed(() => route.params.id as string);

// 新闻详情数据
const newsDetail = ref<NewsDetail | null>(null);
const loading = ref(false);

// 判断是否有上一篇/下一篇
const hasPrevious = computed(() => {
  return newsDetail.value?.lastNewsId
    ? newsDetail.value.lastNewsId.length > 0
    : false;
});

const hasNext = computed(() => {
  return newsDetail.value?.nextNewsId
    ? newsDetail.value.nextNewsId.length > 0
    : false;
});

// 加载新闻详情
const loadNewsDetail = async () => {
  const id = newsId.value;
  if (!id) {
    console.warn("未找到新闻 ID");
    return;
  }

  loading.value = true;
  try {
    const response = await newsApi.getNewsDetail(id);
    console.log("新闻详情响应:", response);

    if (response.code === 200 && response.data) {
      newsDetail.value = response.data;

      // 新闻详情页使用页面数据设置 SEO（不使用 SEO API）
      if (newsDetail.value.title) {
        document.title = newsDetail.value.title;
      }

      // 设置 description（直接使用新闻的 desc 字段）
      if (newsDetail.value.desc) {
        setMeta("description", newsDetail.value.desc);
      }

      // 数据加载完成后，等待 DOM 更新后重新观察动画元素
      await nextTick();
      // 使用 setTimeout 确保 DOM 完全渲染
      setTimeout(() => {
        observeElements();
      }, 100);
    } else {
      console.warn("新闻详情响应格式异常:", response);
    }
  } catch (error) {
    console.error("加载新闻详情失败:", error);
    if (error instanceof RequestError) {
      console.error("错误信息:", error.message);
    }
  } finally {
    loading.value = false;
  }
};

// 图片加载错误处理
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.src = "../../assets/img/news_img.webp";
};

// 跳转到上一篇
const goToPrevious = () => {
  if (hasPrevious.value && newsDetail.value?.lastNewsId) {
    router.push(`/news/${newsDetail.value.lastNewsId}.html`);
  }
};

// 跳转到下一篇
const goToNext = () => {
  if (hasNext.value && newsDetail.value?.nextNewsId) {
    router.push(`/news/${newsDetail.value.nextNewsId}.html`);
  }
};

// ===== 滚动动画：列表入场效果 =====

const animatedElements = new Set<HTMLElement>();

const observerOptions: IntersectionObserverInit = {
  root: null,
  rootMargin: "0px",
  threshold: 0.1,
};

let observer: IntersectionObserver | null = null;

// 观察动画元素的函数
const observeElements = () => {
  if (!observer) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          !animatedElements.has(entry.target as HTMLElement)
        ) {
          const el = entry.target as HTMLElement;
          el.classList.add("fade-in-visible");
          animatedElements.add(el);
        }
      });
    }, observerOptions);
  }

  const elements = document.querySelectorAll(
    ".scroll-fade-in, .scroll-fade-in-left, .scroll-fade-in-right"
  );
  elements.forEach((el) => {
    if (observer && !animatedElements.has(el as HTMLElement)) {
      // 检查元素是否已经在视口内
      const rect = el.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (isVisible) {
        // 如果元素已经在视口内，立即添加动画类
        el.classList.add("fade-in-visible");
        animatedElements.add(el as HTMLElement);
      } else {
        // 否则观察元素
        observer.observe(el);
      }
    }
  });
};

// 监听路由变化，重新加载数据
watch(
  () => route.params.id,
  () => {
    loadNewsDetail();
  },
  { immediate: false }
);

onMounted(() => {
  // 初始加载数据
  loadNewsDetail();

  // 初始观察元素
  observeElements();
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>

<style scoped>
.news-content {
  line-height: 1.8;
  color: #333333;
}

.news-content :deep(p) {
  color: #333333;
  font-size: 14px;
  line-height: 1.8;
  margin-bottom: 1rem;
}

@media (min-width: 640px) {
  .news-content :deep(p) {
    font-size: 16px;
  }
}

@media (min-width: 768px) {
  .news-content :deep(p) {
    font-size: 18px;
  }
}

/* 滚动动画效果 */
.scroll-fade-in {
  opacity: 0;
  transform: translateY(50px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.scroll-fade-in.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

.nav-btn {
  background-color: #1d3c7d;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 100px;
}

@media (min-width: 640px) {
  .nav-btn {
    padding: 11px 26px;
    font-size: 15px;
    min-width: 110px;
  }
}

@media (min-width: 768px) {
  .nav-btn {
    padding: 12px 32px;
    font-size: 16px;
    min-width: 120px;
  }
}

.nav-btn:hover:not(.disabled) {
  background-color: #2a4f9e;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(29, 60, 125, 0.3);
}

.nav-btn.disabled {
  background-color: #e5e5e5;
  color: #999999;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>

